import { DialogType } from 'components/core/Dialog/common/Dialog';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { submitServerChange } from 'components/dataCreationForms/common';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys, DialogTranslationKeys } from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';
import { reportClient } from 'shared/domain/report/reportClient';
import type { CreateReportModel } from 'shared/domain/report/types/model/createReportPayload';

interface SubmitReportOptions<TModel extends CreateReportModel> {
  reportModel: TModel;
  onBeforeSubmit?: () => void;
}

export function useReportDialogBase<TModel extends CreateReportModel>(
  submitEventType: string
) {
  const [open, setOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const confirmCancel = useCancelConfirmation('report');
  const createDialog = useDialog();

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const closeDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(submitEventType));
  }, [submitEventType]);

  const showSuccessDialog = useCallback(() => {
    createDialog({
      catchOnCancel: false,
      title: <FormattedMessage id={DialogTranslationKeys.reportSentTitle} />,
      description: (
        <span>
          <FormattedMessage id={DialogTranslationKeys.reportSentMessage} />
        </span>
      ),
      type: DialogType.info,
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.ok),
        translationKeyToString(CommonTranslationKeys.ok),
      ],
    });
  }, [createDialog]);

  const submitReport = useCallback(
    async ({ reportModel, onBeforeSubmit }: SubmitReportOptions<TModel>) => {
      const outcome = await submitServerChange({
        change: async () => {
          onBeforeSubmit?.();
          await reportClient.create(reportModel);
        },
        setOpen,
        setIsPosting,
        action: 'creating report',
      });
      if (outcome.saved) {
        showSuccessDialog();
      }
    },
    [showSuccessDialog]
  );

  return {
    open,
    isPosting,
    openDialog,
    closeDialog,
    releaseSubmitEvent,
    submitReport,
    SUBMIT_EVENT_TYPE: submitEventType,
  };
}

